<template>
  <div class="user-edit-container">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-edit"></i>
          <span>{{ $t('userManagement.editUser') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button @click="handleBack" size="small">
            <i class="el-icon-back"></i> {{ $t('common.back') }}
          </el-button>
        </div>
      </div>
    </div>

    <el-card class="user-edit-card" shadow="hover">
      <el-form
        ref="userForm"
        :model="userForm"
        :rules="userFormRules"
        label-position="top"
        size="large"
      >
        <div class="form-module">
          <div class="module-title">{{ $t('userManagement.basicInfo') }}</div>
          <el-row :gutter="20">
            <el-col :span="24">
              <el-form-item :label="$t('userManagement.avatar')" prop="avatar">
                <avatar-upload v-model="userForm.avatar"></avatar-upload>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.username')" prop="username">
                <el-input v-model="userForm.username" :disabled="userForm.username === 'admin'" :placeholder="$t('userManagement.enterUsername')" clearable />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.name')" prop="name">
                <el-input v-model="userForm.name" :placeholder="$t('userManagement.inputName')" clearable />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.gender')" prop="gender">
                <el-radio-group v-model="userForm.gender">
                  <el-radio :label="1">{{ $t('userManagement.male') }}</el-radio>
                  <el-radio :label="2">{{ $t('userManagement.female') }}</el-radio>
                  <el-radio :label="0">{{ $t('userManagement.unknown') }}</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.email')" prop="email">
                <el-input v-model="userForm.email" :placeholder="$t('userManagement.inputEmail')" clearable />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.phone')" prop="mobile">
                <el-input v-model="userForm.mobile" :placeholder="$t('userManagement.enterPhone')" clearable />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <div class="form-module">
          <div class="module-title">{{ $t('roleManagement.permissionConfiguration') }}</div>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.role')" prop="roleId">
                <el-select v-model="userForm.roleId" :disabled="userForm.username === 'admin' || rolesLoadFailed" :placeholder="rolesLoadFailed ? $t('userManagement.noRolePermission') : $t('userManagement.selectRole')" style="width: 100%" filterable>
                  <el-option
                    v-for="role in availableRoles"
                    :key="role.id"
                    :label="role.roleName"
                    :value="role.id"
                  ></el-option>
                </el-select>
                <div v-if="rolesLoadFailed" class="el-form-item__error" style="position: static; margin-top: 4px;">
                  {{ $t('userManagement.noRolePermission') }}
                </div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.userGroups')" prop="groupId">
                <el-select v-model="userForm.groupId" :placeholder="$t('userManagement.selectGroup')" style="width: 100%" filterable clearable>
                  <el-option
                    v-for="group in groups"
                    :key="group.id"
                    :label="group.name"
                    :value="group.id"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('common.status')" prop="status">
                <el-tag :type="userForm.status === 0 ? 'info' : userForm.status === 2 ? 'warning' : 'success'" size="medium" effect="dark">
                  {{ userForm.status === 0 ? $t('common.cancelled') : userForm.status === 2 ? $t('common.frozen') : $t('common.inUse') }}
                </el-tag>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <div class="form-module">
          <div class="module-title">{{ $t('userManagement.userLevel') }} & {{ $t('userManagement.accountBalance') }}</div>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.userLevel')" prop="userLevel">
                <el-select v-model="userForm.userLevel" :placeholder="$t('userManagement.selectUserLevel')" style="width: 100%">
                  <el-option v-for="item in levelOptions" :key="item.levelCode" :label="item.levelCode + ' ' + item.levelName" :value="item.levelCode"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.accountBalance')" prop="accountBalance">
                <el-input-number v-model="userForm.accountBalance" :precision="2" :step="0.1" :min="0" style="width: 100%"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.points')" prop="points">
                <div class="points-operation-wrapper">
                  <el-tag size="medium" effect="plain" type="warning" class="points-display-tag">{{ userForm.points || 0 }}</el-tag>
                  <el-button type="success" size="mini" plain icon="el-icon-plus" @click="handleAddPoints" v-if="hasPermission('sys:points:add')">增加积分</el-button>
                  <el-button type="danger" size="mini" plain icon="el-icon-minus" @click="handleDeductPoints" v-if="hasPermission('sys:points:deduct')">扣除积分</el-button>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <div class="form-module">
          <div class="module-title">{{ $t('userManagement.passwordSettings') }}</div>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.newPassword')" prop="password">
                <el-input v-model="userForm.password" type="password" :placeholder="$t('userManagement.inputNewPassword')" show-password />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.confirmPassword')" prop="confirmPassword">
                <el-input v-model="userForm.confirmPassword" type="password" :placeholder="$t('userManagement.confirmNewPassword')" show-password />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <div class="form-actions">
          <el-button @click="handleBack">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" @click="handleSubmit" v-if="hasPermission('sys:user:update')">{{ $t('common.submit') }}</el-button>
        </div>
      </el-form>
    </el-card>

    <!-- 增加积分弹窗 -->
    <el-dialog title="手动增加积分" :visible.sync="addPointsDialogVisible" width="500px" :close-on-click-modal="false" append-to-body>
      <el-form :model="addPointsForm" label-width="100px" :rules="addPointsFormRules" ref="addPointsForm">
        <el-form-item label="用户名">
          <el-input :value="userForm.username + ' (ID: ' + userForm.userid + ')'" disabled></el-input>
        </el-form-item>
        <el-form-item label="当前积分">
          <el-input :value="userForm.points || 0" disabled></el-input>
        </el-form-item>
        <el-form-item label="增加积分" prop="points">
          <el-input-number v-model="addPointsForm.points" :min="1" :max="999999" controls-position="right" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="操作理由" prop="remark">
          <el-input type="textarea" v-model="addPointsForm.remark" :rows="3" placeholder="请输入增加积分的理由" maxlength="200" show-word-limit></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="addPointsDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="pointsSubmitLoading" @click="submitAddPoints">确认增加</el-button>
      </div>
    </el-dialog>

    <!-- 扣除积分弹窗 -->
    <el-dialog title="手动扣除积分" :visible.sync="deductPointsDialogVisible" width="500px" :close-on-click-modal="false" append-to-body>
      <el-form :model="deductPointsForm" label-width="100px" :rules="deductPointsFormRules" ref="deductPointsForm">
        <el-form-item label="用户名">
          <el-input :value="userForm.username + ' (ID: ' + userForm.userid + ')'" disabled></el-input>
        </el-form-item>
        <el-form-item label="当前积分">
          <el-input :value="userForm.points || 0" disabled></el-input>
        </el-form-item>
        <el-form-item label="扣除积分" prop="points">
          <el-input-number v-model="deductPointsForm.points" :min="1" :max="userForm.points || 0" controls-position="right" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="操作理由" prop="remark">
          <el-input type="textarea" v-model="deductPointsForm.remark" :rows="3" placeholder="请输入扣除积分的理由" maxlength="200" show-word-limit></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="deductPointsDialogVisible = false">取消</el-button>
        <el-button type="danger" :loading="pointsSubmitLoading" @click="submitDeductPoints">确认扣除</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { hasPermission } from '@/utils/permission'
import { getLevelOptions } from '@/apis/system/level'
import { manualAddPoints, manualDeductPoints } from '@/apis/sys/points'
import AvatarUpload from '@/components/AvatarUpload.vue'
import dayjs from 'dayjs'

export default {
  name: 'UserEdit',
  components: {
    AvatarUpload
  },
  data() {
    return {
      loading: false,
      groups: [],
      levelOptions: [],
      rolesLoadFailed: false,
      userForm: {
        userid: null,
        username: '',
        name: '',
        avatar: '',
        email: '',
        mobile: '',
        password: '',
        confirmPassword: '',
        roleId: null,
        groupId: null,
        status: 1,
        gender: 0,
        userLevel: 'V0',
        accountBalance: 0,
        points: 0
      },
      originalUser: {},
      userFormRules: {
        username: [
          { required: true, message: this.$t('userManagement.usernameRequired'), trigger: 'blur' },
          { min: 3, max: 64, message: this.$t('userManagement.usernameLength'), trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9_-]+$/, message: this.$t('userManagement.usernamePattern'), trigger: 'blur' }
        ],
        name: [
          { required: true, message: this.$t('userManagement.nameRequired'), trigger: 'blur' },
          { min: 2, max: 50, message: this.$t('userManagement.nameLength'), trigger: 'blur' }
        ],
        email: [
          { required: false, message: this.$t('userManagement.emailRequired'), trigger: 'blur' },
          { type: 'email', message: this.$t('userManagement.emailPattern'), trigger: 'blur' }
        ],
        mobile: [
          { required: false, message: this.$t('userManagement.phoneRequired'), trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: this.$t('userManagement.phoneInvalid'), trigger: 'blur' }
        ],
        password: [
          { min: 6, max: 20, message: this.$t('userManagement.passwordLength'), trigger: 'blur' }
        ],
        confirmPassword: [
          { validator: this.validateConfirmPassword, trigger: 'blur' }
        ],
        roleId: [
          { required: true, message: this.$t('userManagement.roleRequired'), trigger: 'change' }
        ],
        groupId: [
          { required: false, message: this.$t('userManagement.groupRequired'), trigger: 'change' }
        ],
        userLevel: [
          { required: true, message: this.$t('userManagement.selectUserLevel'), trigger: 'change' }
        ],
        accountBalance: [
          { required: true, message: this.$t('userManagement.accountBalanceRequired'), trigger: 'blur' }
        ]
      },
      // 积分操作相关
      addPointsDialogVisible: false,
      addPointsForm: { points: 1, remark: '' },
      addPointsFormRules: {
        points: [{ required: true, message: '请输入积分数量', trigger: 'blur' }],
        remark: [{ required: true, message: '请输入操作理由', trigger: 'blur' }]
      },
      deductPointsDialogVisible: false,
      deductPointsForm: { points: 1, remark: '' },
      deductPointsFormRules: {
        points: [{ required: true, message: '请输入积分数量', trigger: 'blur' }],
        remark: [{ required: true, message: '请输入操作理由', trigger: 'blur' }]
      },
      pointsSubmitLoading: false
    }
  },
  computed: {
    ...mapGetters(['allRoles']),
    userId() {
      return this.$route.params.id
    },
    availableRoles() {
      return this.allRoles.filter(role => role.roleCode !== 'superAdmin')
    }
  },
  created() {
    this.fetchGroups()
    this.fetchRoles()
    this.loadUserData()
    this.fetchLevelOptions()
  },
  watch: {
    userId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.userForm.password = ''
        this.userForm.confirmPassword = ''
        this.loadUserData()
      }
    }
  },
  methods: {
    hasPermission,
    fetchLevelOptions() {
      getLevelOptions().then(res => {
        this.levelOptions = res || []
      }).catch(() => {})
    },
    // 获取角色列表
    async fetchRoles() {
      if (this.allRoles.length === 0) {
        try {
          await this.$store.dispatch('fetchRoles')
        } catch (error) {
          this.rolesLoadFailed = true
        }
      }
    },
    // 获取分组列表
    async fetchGroups() {
      try {
        const res = await this.$store.dispatch('fetchUserGroups')
        this.groups = res
      } catch (error) {
        this.$message.error(this.$t('userManagement.fetchGroupError'))
      }
    },
    // 加载用户数据
    async loadUserData() {
      try {
        this.originalUser = await this.$store.dispatch('fetchUserById', this.userId)
      } catch (error) {
        const msg = error?.code === 403
          ? this.$t('userManagement.noPermissionView')
          : this.$t('userManagement.userNotFound')
        this.$message.error(msg)
        this.$router.push('/user')
        return
      }
      
      if (!this.originalUser) {
        this.$message.error(this.$t('userManagement.userNotFound'))
        this.$router.push('/user')
        return
      }
      
      this.userForm = {
        userid: this.originalUser.userid,
        username: this.originalUser.username,
        name: this.originalUser.name,
        avatar: this.originalUser.avatar || 'default:1',
        email: this.originalUser.email,
        mobile: this.originalUser.mobile,
        roleId: this.originalUser.roleId,
        groupId: (this.originalUser.groupIdList && this.originalUser.groupIdList.length > 0) ? this.originalUser.groupIdList[0] : (this.originalUser.groupIds && this.originalUser.groupIds.length > 0 ? this.originalUser.groupIds[0] : null),
        gender: (this.originalUser.gender !== undefined && this.originalUser.gender !== null) ? Number(this.originalUser.gender) : 0,
        status: this.originalUser.status,
        userLevel: this.originalUser.userLevel || 'V0',
        accountBalance: this.originalUser.accountBalance || 0,
        points: this.originalUser.points || 0,
        password: '',
        confirmPassword: ''
      }
    },
    
    // 验证确认密码
    validateConfirmPassword(rule, value, callback) {
      if (this.userForm.password && value !== this.userForm.password) {
        callback(new Error(this.$t('userManagement.passwordNotMatch')))
      } else {
        callback()
      }
    },

    // 提交表单
    handleSubmit() {
      this.$refs.userForm.validate((valid) => {
        if (valid) {
          // 移除确认密码字段
          const userData = { ...this.userForm }
          delete userData.confirmPassword
          
          // 如果密码为空，不更新密码
          if (!userData.password) {
            delete userData.password
          }

          // 确保使用原始的 userid，不强制转换为 Number，以防大数精度丢失或非数字 ID 格式问题
          if (userData.userid) {
            userData.userid = String(userData.userid)
          }
          
          // 将单选的 groupId 转换为后端需要的 groupIdList 数组
          if (userData.groupId) {
            userData.groupIdList = [userData.groupId]
            delete userData.groupId
          } else {
            userData.groupIdList = []
          }
          
          // 提交用户数据
          this.$store.dispatch('updateUser', userData).then(() => {
            this.$message.success(this.$t('userManagement.updateSuccess'))
            this.handleBack()
          }).catch(err => {
            console.error('Update user failed:', err)
            this.$message.error(this.$t('userManagement.updateError'))
          })
        } else {
          return false
        }
      })
    },
    
    // 重置表单
    handleReset() {
      if (this.originalUser) {
        this.userForm = {
          userid: this.originalUser.userid,
          username: this.originalUser.username,
          name: this.originalUser.name,
          avatar: this.originalUser.avatar || 'default:1',
          email: this.originalUser.email,
          mobile: this.originalUser.mobile,
          roleId: this.originalUser.roleId,
          groupId: (this.originalUser.groupIdList && this.originalUser.groupIdList.length > 0) ? this.originalUser.groupIdList[0] : (this.originalUser.groupIds && this.originalUser.groupIds.length > 0 ? this.originalUser.groupIds[0] : null),
          gender: (this.originalUser.gender !== undefined && this.originalUser.gender !== null) ? Number(this.originalUser.gender) : 0,
          status: this.originalUser.status,
          userLevel: this.originalUser.userLevel || 'V0',
          accountBalance: this.originalUser.accountBalance || 0,
          points: this.originalUser.points || 0,
          password: '',
          confirmPassword: ''
        }
        this.$nextTick(() => {
          this.$refs.userForm.clearValidate()
        })
      }
    },
    
    // 打开增加积分弹窗
    handleAddPoints() {
      this.addPointsForm = { points: 1, remark: '' }
      this.addPointsDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.addPointsForm) {
          this.$refs.addPointsForm.clearValidate()
        }
      })
    },
    // 打开扣除积分弹窗
    handleDeductPoints() {
      if ((this.userForm.points || 0) <= 0) {
        this.$message.warning('当前用户积分为0，无法扣除')
        return
      }
      this.deductPointsForm = { points: 1, remark: '' }
      this.deductPointsDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.deductPointsForm) {
          this.$refs.deductPointsForm.clearValidate()
        }
      })
    },
    // 提交增加积分
    submitAddPoints() {
      this.$refs.addPointsForm.validate(valid => {
        if (!valid) return
        this.pointsSubmitLoading = true
        const params = {
          userId: this.userForm.userid,
          points: this.addPointsForm.points,
          remark: this.addPointsForm.remark
        }
        manualAddPoints(params).then(() => {
          this.$message.success('积分增加成功')
          this.addPointsDialogVisible = false
          this.userForm.points = (this.userForm.points || 0) + this.addPointsForm.points
          if (this.originalUser) {
            this.originalUser.points = this.userForm.points
          }
        }).catch(err => {
          this.$message.error(err.message || '积分增加失败')
        }).finally(() => {
          this.pointsSubmitLoading = false
        })
      })
    },
    // 提交扣除积分
    submitDeductPoints() {
      this.$refs.deductPointsForm.validate(valid => {
        if (!valid) return
        const currentPoints = this.userForm.points || 0
        if (this.deductPointsForm.points > currentPoints) {
          this.$message.warning('扣除积分不能超过当前积分')
          return
        }
        this.pointsSubmitLoading = true
        const params = {
          userId: this.userForm.userid,
          points: this.deductPointsForm.points,
          remark: this.deductPointsForm.remark
        }
        manualDeductPoints(params).then(() => {
          this.$message.success('积分扣除成功')
          this.deductPointsDialogVisible = false
          this.userForm.points = currentPoints - this.deductPointsForm.points
          if (this.originalUser) {
            this.originalUser.points = this.userForm.points
          }
        }).catch(err => {
          this.$message.error(err.message || '积分扣除失败')
        }).finally(() => {
          this.pointsSubmitLoading = false
        })
      })
    },

    // 返回用户列表
    handleBack() {
      this.$router.back()
    }
  }
}
</script>

<style scoped lang="scss">
.user-edit-container {
  padding: 0;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;

  /* 隐藏滚动条 */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

.control-panel {
  margin-bottom: 24px;
}

.control-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #606266;
  font-size: 14px;
  
  i {
    font-size: 18px;
    color: #409eff;
  }
}

.action-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-edit-card {
  margin-bottom: 20px;
  border-radius: 8px;
}

.form-module {
  margin-bottom: 30px;
}

.module-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 20px;
  padding-left: 10px;
  border-left: 4px solid #409eff;
  line-height: 1;
}

.form-actions {
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
  text-align: right;
}

.form-actions button {
  min-width: 100px;
  margin-left: 15px;
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: #606266;
  padding-bottom: 8px;
}

:deep(.el-input__inner), :deep(.el-textarea__inner) {
  border-radius: 4px;
}

.points-operation-wrapper {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  .points-display-tag {
    font-size: 14px;
    font-weight: 600;
    padding: 0 12px;
    min-width: 50px;
    text-align: center;
  }
}

@media screen and (max-width: 768px) {
  .control-container {
    flex-direction: row;
    align-items: center;
  }
  
  .header-info span {
    display: none;
  }
}
</style>